/**
 * IntegrationSettings.jsx — iter171
 *
 * Self-serve integration setup for company admins/owners. Renders a grid of
 * provider cards (Shopify, Etsy, Amazon, Sendle, AusPost, ShipStation, Stripe,
 * Resend, SendGrid). Clicking a card opens a Sheet with fields defined by the
 * backend catalog; Save + Test buttons round-trip through `/api/integrations`.
 *
 * Secrets are stored encrypted at rest and returned to the UI masked as
 * `••••abcd`, so re-submitting the form with the masked value keeps the
 * existing secret intact.
 */
import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plug, CheckCircle, XCircle, Warning, ArrowSquareOut, Trash, FloppyDisk, Play, ShoppingCart, Truck, CreditCard, Envelope,
  ClockCounterClockwise, PencilSimple, Power, TestTube,
} from '@phosphor-icons/react';
import {
  getIntegrationCatalog, listIntegrationConfigs, upsertIntegrationConfig,
  toggleIntegrationConfig, testIntegrationConfig, deleteIntegrationConfig,
  listIntegrationEvents,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';

const CATEGORY_META = {
  ecommerce: { label: 'E-commerce', icon: ShoppingCart },
  shipping:  { label: 'Shipping',   icon: Truck },
  payments:  { label: 'Payments',   icon: CreditCard },
  email:     { label: 'Email',      icon: Envelope },
};

export default function IntegrationSettings() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProvider, setEditingProvider] = useState(null);
  const [formCreds, setFormCreds] = useState({});
  const [formSettings, setFormSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [busyToggleProvider, setBusyToggleProvider] = useState(null);
  const [events, setEvents] = useState([]);

  const isCompanyAdmin =
    user && (
      user.company_role === 'owner' ||
      user.company_role === 'admin' ||
      user.platform_role === 'superadmin' ||
      user.role === 'admin'
    );

  useEffect(() => {
    if (!isCompanyAdmin) return;
    (async () => {
      try {
        const [c, cfg, evs] = await Promise.all([
          getIntegrationCatalog(),
          listIntegrationConfigs(),
          listIntegrationEvents(20),
        ]);
        setCatalog(c.data.providers || []);
        setConfigs(cfg.data.configs || []);
        setEvents(evs.data.events || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load integrations');
      } finally {
        setLoading(false);
      }
    })();
  }, [isCompanyAdmin]);

  const configByProvider = useMemo(
    () => Object.fromEntries(configs.map((c) => [c.provider, c])),
    [configs],
  );
  const catalogByProvider = useMemo(
    () => Object.fromEntries(catalog.map((c) => [c.provider, c])),
    [catalog],
  );

  const grouped = useMemo(() => {
    const g = {};
    for (const cat of catalog) {
      (g[cat.category] = g[cat.category] || []).push(cat);
    }
    return g;
  }, [catalog]);

  if (user && !isCompanyAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const refreshConfigs = async () => {
    const [cfg, evs] = await Promise.all([
      listIntegrationConfigs(),
      listIntegrationEvents(20),
    ]);
    setConfigs(cfg.data.configs || []);
    setEvents(evs.data.events || []);
  };

  const openEditor = (provider) => {
    const cat = catalogByProvider[provider];
    const cfg = configByProvider[provider] || { credentials: {}, settings: {} };
    // Prefill fields — secret fields come back masked, non-secret return their plaintext.
    const creds = {};
    for (const f of cat.credential_fields) creds[f.key] = cfg.credentials?.[f.key] || '';
    const settings = {};
    for (const f of (cat.setting_fields || [])) {
      const stored = cfg.settings?.[f.key];
      settings[f.key] = stored !== undefined ? stored : (f.default ?? (f.type === 'boolean' ? false : ''));
    }
    setFormCreds(creds);
    setFormSettings(settings);
    setEditingProvider(provider);
  };

  const handleSave = async () => {
    if (!editingProvider) return;
    setSaving(true);
    try {
      await upsertIntegrationConfig(editingProvider, {
        credentials: formCreds,
        settings: formSettings,
      });
      toast.success('Saved');
      await refreshConfigs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!editingProvider) return;
    setTesting(true);
    try {
      // Save first so credentials are up to date, then run the test.
      await upsertIntegrationConfig(editingProvider, {
        credentials: formCreds,
        settings: formSettings,
      });
      const r = await testIntegrationConfig(editingProvider);
      if (r.data.ok) toast.success(r.data.message || 'Connection OK');
      else toast.error(r.data.message || 'Connection failed');
      await refreshConfigs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleToggle = async (provider) => {
    setBusyToggleProvider(provider);
    try {
      await toggleIntegrationConfig(provider);
      await refreshConfigs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Toggle failed');
    } finally {
      setBusyToggleProvider(null);
    }
  };

  const handleDelete = async () => {
    if (!editingProvider) return;
    if (!window.confirm(`Remove all saved credentials for ${catalogByProvider[editingProvider]?.name}? This cannot be undone.`)) return;
    try {
      await deleteIntegrationConfig(editingProvider);
      toast.success('Integration removed');
      setEditingProvider(null);
      await refreshConfigs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const editingCatalog = editingProvider ? catalogByProvider[editingProvider] : null;
  const editingConfig = editingProvider ? configByProvider[editingProvider] : null;

  return (
    <Layout>
      <div className="max-w-6xl space-y-6" data-testid="integration-settings-page">
        <div>
          <p className="overline">Data &amp; Integration</p>
          <h1 className="text-2xl font-bold">Integrations</h1>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Connect your own accounts for e-commerce, shipping, payments, and email. Credentials are encrypted at rest
            and only ever visible to your company&apos;s owners and admins.
          </p>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading integrations…</p>
        ) : (
          Object.entries(grouped).map(([category, providers]) => {
            const Meta = CATEGORY_META[category] || { label: category, icon: Plug };
            const Icon = Meta.icon;
            return (
              <section key={category} className="space-y-3" data-testid={`integration-group-${category}`}>
                <div className="flex items-center gap-2">
                  <Icon size={18} weight="fill" className="text-brand" />
                  <h2 className="text-sm font-bold uppercase tracking-widest">{Meta.label}</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {providers.map((p) => {
                    const cfg = configByProvider[p.provider];
                    return (
                      <IntegrationCard
                        key={p.provider}
                        provider={p}
                        config={cfg}
                        onOpen={() => openEditor(p.provider)}
                        onToggle={() => handleToggle(p.provider)}
                        busy={busyToggleProvider === p.provider}
                      />
                    );
                  })}
                </div>
              </section>
            );
          })
        )}

        {!loading && events.length > 0 && (
          <section className="space-y-3 pt-4 border-t border-border" data-testid="integration-activity">
            <div className="flex items-center gap-2">
              <ClockCounterClockwise size={18} weight="fill" className="text-brand" />
              <h2 className="text-sm font-bold uppercase tracking-widest">Recent activity</h2>
              <span className="text-[10px] text-muted-foreground">Last {events.length} events across all integrations</span>
            </div>
            <div className="border border-border" data-testid="integration-activity-list">
              {events.map((ev) => {
                const cat = catalogByProvider[ev.provider];
                return <ActivityRow key={ev.id} event={ev} providerName={cat?.name || ev.provider} />;
              })}
            </div>
          </section>
        )}
      </div>

      <Dialog open={!!editingProvider} onOpenChange={(o) => { if (!o) setEditingProvider(null); }}>
        <DialogContent className="max-w-xl" data-testid="integration-editor-dialog">
          {editingCatalog && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Plug size={18} weight="fill" className="text-brand" />
                  {editingCatalog.name}
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  {editingCatalog.description}{' '}
                  {editingCatalog.docs_url && (
                    <a
                      href={editingCatalog.docs_url} target="_blank" rel="noreferrer"
                      className="text-brand inline-flex items-center gap-1 hover:underline"
                    >
                      Docs <ArrowSquareOut size={11} />
                    </a>
                  )}
                </p>
              </DialogHeader>

              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                <div className="space-y-3">
                  <div className="overline">Credentials</div>
                  {editingCatalog.credential_fields.map((f) => (
                    <FieldRow
                      key={f.key}
                      field={f}
                      value={formCreds[f.key] || ''}
                      onChange={(v) => setFormCreds({ ...formCreds, [f.key]: v })}
                    />
                  ))}
                </div>

                {editingCatalog.setting_fields?.length > 0 && (
                  <div className="space-y-3 pt-4 border-t border-border">
                    <div className="overline">Settings</div>
                    {editingCatalog.setting_fields.map((f) => (
                      <FieldRow
                        key={f.key}
                        field={f}
                        value={formSettings[f.key] ?? (f.type === 'boolean' ? false : '')}
                        onChange={(v) => setFormSettings({ ...formSettings, [f.key]: v })}
                      />
                    ))}
                  </div>
                )}

                {editingConfig?.last_test_result && (
                  <div className={`p-3 border text-xs ${editingConfig.last_test_result.ok ? 'border-emerald-300 bg-emerald-50/50 text-emerald-900' : 'border-red-300 bg-red-50/50 text-red-900'}`}>
                    <div className="flex items-center gap-2 font-bold">
                      {editingConfig.last_test_result.ok ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
                      Last test result
                    </div>
                    <p className="mt-1">{editingConfig.last_test_result.message}</p>
                  </div>
                )}
              </div>

              <DialogFooter className="justify-between flex-row gap-2">
                <Button
                  variant="outline" onClick={handleDelete}
                  className="text-red-700 border-red-200 hover:bg-red-50"
                  disabled={!editingConfig?.has_credentials}
                  data-testid="integration-delete-btn"
                >
                  <Trash size={14} className="mr-1" /> Remove
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline" onClick={handleTest} disabled={testing || saving}
                    data-testid="integration-test-btn"
                  >
                    <Play size={14} className="mr-1" />
                    {testing ? 'Testing…' : 'Test connection'}
                  </Button>
                  <Button
                    onClick={handleSave} disabled={saving || testing}
                    className="bg-brand hover:bg-brand/90"
                    data-testid="integration-save-btn"
                  >
                    <FloppyDisk size={14} className="mr-1" />
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

const IntegrationCard = ({ provider, config, onOpen, onToggle, busy }) => {
  const enabled = !!config?.enabled;
  const hasCreds = !!config?.has_credentials;
  const testResult = config?.last_test_result;

  return (
    <div
      className="border border-border bg-card p-4 flex flex-col gap-3 hover:border-brand/60 transition-colors"
      data-testid={`integration-card-${provider.provider}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-base font-bold truncate">{provider.name}</h3>
          <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{provider.description}</p>
        </div>
        <StatusBadge enabled={enabled} hasCreds={hasCreds} />
      </div>

      {testResult && (
        <div className={`text-[11px] flex items-center gap-1 ${testResult.ok ? 'text-emerald-700' : 'text-red-700'}`}>
          {testResult.ok ? <CheckCircle size={12} weight="fill" /> : <XCircle size={12} weight="fill" />}
          <span className="truncate">{testResult.message}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 mt-auto pt-2 border-t border-border/60">
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <Switch
            checked={enabled}
            disabled={busy || !hasCreds}
            onCheckedChange={onToggle}
            data-testid={`integration-toggle-${provider.provider}`}
          />
          <span>{enabled ? 'Enabled' : 'Disabled'}</span>
        </div>
        <Button
          variant="outline" size="sm" onClick={onOpen}
          data-testid={`integration-configure-${provider.provider}`}
        >
          {hasCreds ? 'Edit' : 'Configure'}
        </Button>
      </div>
    </div>
  );
};

const StatusBadge = ({ enabled, hasCreds }) => {
  if (enabled) {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
        <CheckCircle size={11} weight="fill" className="mr-1" /> On
      </Badge>
    );
  }
  if (hasCreds) {
    return (
      <Badge variant="outline" className="border-amber-300 text-amber-800">
        <Warning size={11} weight="fill" className="mr-1" /> Off
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-muted-foreground">
      Not set up
    </Badge>
  );
};

const FieldRow = ({ field, value, onChange }) => {
  if (field.type === 'boolean') {
    return (
      <div className="flex items-center justify-between gap-3">
        <div>
          <Label className="text-xs">{field.label}</Label>
          {field.help && <p className="text-[10px] text-muted-foreground mt-0.5">{field.help}</p>}
        </div>
        <Switch
          checked={!!value}
          onCheckedChange={onChange}
          data-testid={`integration-field-${field.key}`}
        />
      </div>
    );
  }
  return (
    <div>
      <Label htmlFor={`fld-${field.key}`} className="text-xs">
        {field.label}
        {field.required && <span className="text-red-600 ml-0.5">*</span>}
      </Label>
      <Input
        id={`fld-${field.key}`}
        type={field.type === 'password' ? 'password' : 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.secret ? '(hidden — leave masked to keep existing)' : ''}
        className="mt-1"
        data-testid={`integration-field-${field.key}`}
        autoComplete={field.type === 'password' ? 'new-password' : 'off'}
      />
      {field.help && <p className="text-[10px] text-muted-foreground mt-1">{field.help}</p>}
    </div>
  );
};


const EVENT_META = {
  saved:    { Icon: PencilSimple,      label: 'Saved',    color: 'text-slate-700' },
  enabled:  { Icon: Power,             label: 'Enabled',  color: 'text-emerald-700' },
  disabled: { Icon: Power,             label: 'Disabled', color: 'text-amber-700' },
  tested:   { Icon: TestTube,          label: 'Tested',   color: 'text-blue-700' },
  deleted:  { Icon: Trash,             label: 'Removed',  color: 'text-red-700' },
};

const relativeTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(1, Math.round((now - then) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const m = Math.round(diffSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

const ActivityRow = ({ event, providerName }) => {
  const meta = EVENT_META[event.event_type] || { Icon: Plug, label: event.event_type, color: 'text-slate-700' };
  const Icon = meta.Icon;
  const badColour = event.event_type === 'tested' && !event.ok;
  return (
    <div
      className="flex items-start gap-3 px-3 py-2 border-b border-border last:border-b-0 text-xs"
      data-testid={`integration-activity-row-${event.id}`}
    >
      <div className={`mt-0.5 shrink-0 ${badColour ? 'text-red-700' : meta.color}`}>
        {badColour ? <XCircle size={14} weight="fill" /> : <Icon size={14} weight="fill" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-bold">{providerName}</span>
          <span className={`uppercase text-[10px] tracking-widest ${badColour ? 'text-red-700' : meta.color}`}>
            {badColour ? 'Test failed' : meta.label}
          </span>
          <span className="text-muted-foreground text-[10px]">by {event.actor_email || 'system'}</span>
        </div>
        {event.message && (
          <p className={`mt-0.5 truncate ${badColour ? 'text-red-700' : 'text-muted-foreground'}`}>
            {event.message}
          </p>
        )}
      </div>
      <div className="text-[10px] text-muted-foreground shrink-0 tabular-nums" title={event.created_at}>
        {relativeTime(event.created_at)}
      </div>
    </div>
  );
};
